import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { MapPin, PackageCheck } from "lucide-react";
import { StatusPill } from "@/components/brand";
import MapView from "@/components/MapView";
import api from "@/lib/api";
import Footer from "@/components/Footer";

export default function TrackDelivery() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/track/${token}`);
      setD(data);
    } catch {
      setErr(true);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.ref) document.title = `Routa · Order ${d.ref}`;
    else if (err) document.title = "Routa · Tracking link not found";
    else document.title = "Routa · Tracking";
  }, [d, err]);
  const inTransit = ["assigned", "picked_up", "en_route"].includes(d?.status);
  useEffect(() => {
    if (!inTransit) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [inTransit, load]);

  if (err) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="track-not-found">
        <div className="text-center">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-3">Routa · Tracking</div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0a0a0a] mb-2">Link not found</h1>
          <p className="text-sm text-gray-500">This tracking link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  if (!d) return <div className="min-h-screen bg-white flex items-center justify-center text-sm text-gray-400 font-mono uppercase tracking-wide">Loading…</div>;

  const loc = inTransit ? d.driver_location : null;
  const delivered = d.status === "delivered";
  const points = [
    { lat: d.dropoff.lat, lng: d.dropoff.lng, label: `Dropoff · ${d.dropoff.address}`, type: "dropoff" },
    ...(loc ? [{ lat: loc.lat, lng: loc.lng, label: `${d.driver_name || "Driver"} · ${new Date(loc.at).toLocaleTimeString()}`, type: "driver", eta: d.eta_min }] : []),
  ];
  const trails = loc && d.driver_trail ? [d.driver_trail] : [];

  return (
    <div className="min-h-screen bg-white" data-testid="track-page">
      <header className="border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#0a0a0a]">ROUTA</div>
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">Order · {d.ref}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-4 mb-2">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Delivery from {d.merchant_name}</span>
          <StatusPill status={d.status} />
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-[#0a0a0a] mb-8">
          {delivered ? "Your delivery has arrived" : inTransit && d.eta_min != null ? `Arriving in ~${d.eta_min} min` : "Your delivery is on the way"}
        </h1>

        <div className="border border-gray-200 bg-white p-4 mb-6 flex flex-wrap items-center gap-4" data-testid="track-status-bar">
          {delivered ? (
            <span className="flex items-center gap-2 text-sm text-[#0a0a0a]"><PackageCheck className="h-4 w-4 text-orange-500" /> Delivered</span>
          ) : (
            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500">
              <span className={`h-2 w-2 rounded-full ${loc ? "bg-orange-500 animate-pulse" : "bg-gray-300"}`} />
              {loc ? `Live · driver ${d.driver_name || ""} seen ${new Date(loc.at).toLocaleTimeString()}` : "Waiting for driver to start"}
            </span>
          )}
          {inTransit && d.eta_min != null && (
            <span className="ml-auto text-orange-600 font-mono text-[11px] tracking-[0.14em] uppercase" data-testid="track-eta">~{d.eta_min} min · {d.distance_km} km away</span>
          )}
        </div>

        <MapView points={points} trails={trails} height={360} follow={inTransit} />

        <div className="mt-6 border border-gray-200 p-5" data-testid="track-dropoff">
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 mb-2"><MapPin className="h-3.5 w-3.5" /> Dropoff</div>
          <div className="text-sm text-[#0a0a0a]">{d.dropoff.address}</div>
        </div>

        <div className="mt-6 border border-gray-200 p-5">
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-4">Progress</div>
          <ol className="space-y-4" data-testid="track-timeline">
            {d.timeline.map((t, i) => (
              <li key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className={`h-2.5 w-2.5 rounded-full ${i === d.timeline.length - 1 ? "bg-orange-500" : "bg-[#0a0a0a]"}`} />
                  {i < d.timeline.length - 1 && <span className="w-px flex-1 bg-gray-200 my-1" />}
                </div>
                <div>
                  <div className="text-sm text-[#0a0a0a] capitalize">{t.status.replace("_", " ")}</div>
                  <div className="font-mono text-[10px] text-gray-400">{new Date(t.at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-10 text-center font-mono text-[10px] tracking-[0.18em] uppercase text-gray-300">Powered by Routa</div>
      </main>
      <Footer />
    </div>
  );
}
